import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { useSEO } from "../utils/seo";
import { getAttributionFields } from "../utils/utm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PROJECT_TYPE_OPTIONS = [
  "PathScore First Look",
  "PathScore",
  "AreaScore",
  "Managed Land Projects",
  "Contract Land Professionals",
  "Right-of-Way Project Management",
  "Easement Acquisition",
  "Renewable Energy Land Services",
  "Field Access / Survey Support Workflows",
  "Title / Ownership Support",
  "GIS / Parcel Intelligence",
  "Other",
];

const SERVICE_OPTIONS = [
  "Complimentary PathScore First Look",
  "Full PathScore engagement",
  "AreaScore development-area screening",
  "Right-of-way project management",
  "Easement acquisition & landowner negotiation",
  "Title / ownership research",
  "GIS & parcel tracking",
  "Survey support workflows",
  "Field access coordination",
  "Project controls & reporting",
  "Not sure yet",
];

const PROJECT_STAGE_OPTIONS = [
  "Idea / feasibility",
  "Route or site selection",
  "Pre-permitting",
  "Permitting in progress",
  "Land acquisition underway",
  "Construction / execution",
  "Post-construction / operations",
];

const PREFERRED_CONTACT_OPTIONS = ["Email", "Phone", "Either"];

/**
 * Contact page, main lead-capture form.
 *
 * Supports preselection of project_type via URL query param:
 *   /contact?type=PathScore
 *   /contact?type=AreaScore
 *
 * `variant="corridor"` renders the same shell for /score-a-corridor
 * with PathScore-forward copy.
 */
const Contact = ({ variant = "default" }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const typeFromQuery = variant === "corridor" ? "PathScore First Look" : (params.get("type") || "");
  const sourceFromQuery = params.get("source") || (variant === "corridor" ? "score_a_corridor_page" : "contact_page");

  const isCorridor = variant === "corridor";

  useSEO({
    title: isCorridor
      ? "Complimentary PathScore First Look | Onward Land Solutions"
      : "Talk to Onward | Contact | Onward Land Solutions",
    description: isCorridor
      ? "Get a complimentary PathScore First Look for preliminary route screening. Full PathScore is separately scoped paid work. Onward confirms scope, confidentiality and next steps."
      : "Talk to Onward about route and site evaluation, title, land acquisition, permitting coordination, GIS mapping and project staffing.",
    canonicalPath: isCorridor ? "/score-a-corridor" : "/contact",
  });

  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    project_type: typeFromQuery,
    service_needed: variant === "corridor" ? "Complimentary PathScore First Look" : "",
    location: "",
    acreage: "",
    project_stage: "",
    alternatives: "",
    timeline: "",
    preferred_contact: "",
    message: "",
    nda_requested: false,
    route_available: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // "sent" | "failed" | null
  const [error, setError] = useState(null);
  const submittedType = useRef("");

  useEffect(() => {
    setFormData((d) => ({ ...d, project_type: typeFromQuery, service_needed: isCorridor ? "Complimentary PathScore First Look" : "" }));
    setSubmitted(false);
    setError(null);
  }, [typeFromQuery, isCorridor]);

  useEffect(() => {
    if (submitted) {
      const eventParams = { form_name: isCorridor ? "score_a_corridor" : "contact", project_type: submittedType.current, form_source: sourceFromQuery };
      if (typeof window.gtag === "function") window.gtag("event", "generate_lead", { ...eventParams, send_to: "G-Y57BJQX5P0" });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "generate_lead", ...eventParams });
    }
  }, [submitted, isCorridor, sourceFromQuery]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.message.trim()) {
      setError("Please enter your name and a brief project description.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/consultation`, {
        ...formData,
        name: formData.name.trim(),
        message: formData.message.trim(),
        ...getAttributionFields(),
        form_source: sourceFromQuery,
        page_url: typeof window !== "undefined" ? window.location.href : null,
      });
      submittedType.current = formData.project_type || formData.service_needed || "Contact";
      setEmailStatus(response?.data?.email_delivery_status || null);
      setSubmitted(true);
    } catch (err) {
      setError("There was an error submitting your request. Please try again or call us directly at (817) 577-5024.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    const emailFailed = emailStatus === "failed";
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-16" style={{ background: "var(--ols-limestone)" }}>
        <div className="max-w-2xl text-center" data-testid="contact-success">
          <CheckCircle2 className="h-16 w-16 mx-auto mb-6" style={{ color: "#C8102E" }} />
          <h1 className="ols-h2 mb-4">{emailFailed ? "Received. Please call to confirm." : "Thank you."}</h1>
          {emailFailed ? (
            <>
              <p className="ols-lede mb-4">
                Your project information was saved in our system, but our automated notification email did not
                go through. Please call us at (817) 577-5024 so we can be sure we see your project.
              </p>
              <p className="text-[13px] mb-8" style={{ color: "var(--ols-body-muted)" }}>
                Reference: your submission is stored. No need to fill the form again.
              </p>
            </>
          ) : (
            <p className="ols-lede mb-8">
              Your request has been received. An Onward representative will follow up to confirm scope, confidentiality and next steps.
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/" className="ols-btn-secondary ols-btn-secondary--dark" data-testid="contact-success-home">Back to Home</Link>
            <a href="tel:+18175775024" className="ols-btn-primary" data-testid="contact-success-phone">Call (817) 577-5024</a>
          </div>
        </div>
      </div>
    );
  }

  const heroTitle = isCorridor ? "Complimentary PathScore First Look" : "Talk to Onward";
  const heroLede = isCorridor
    ? "A preliminary corridor overview, mapping and crossing questions, with agreed map/GIS deliverables. We confirm scope and confidentiality first."
    : "Tell us what you need. We can help with route and site evaluation, title, land acquisition, permitting coordination, GIS mapping and project staffing.";

  return (
    <>
      <section style={{ background: "linear-gradient(180deg, #090A0C 0%, #15171B 100%)" }} className="pt-16 pb-12 relative overflow-hidden" data-testid="contact-hero">
        <div className="ols-parcel-grid absolute inset-0" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {!isCorridor && <p className="ols-eyebrow ols-eyebrow--light mb-4">Contact</p>}
          <h1 className="ols-h1 max-w-4xl" data-testid="contact-heading">{heroTitle}</h1>
          <p className="text-lg text-slate-200 max-w-3xl mt-6">{heroLede}</p>
          {isCorridor && <p className="text-sm text-slate-200 max-w-3xl mt-4" data-testid="first-look-offer-scope">Complimentary preliminary screening within PathScore. Full PathScore is separately scoped paid work.</p>}
        </div>
      </section>

      <section className="ols-section" style={{ background: "var(--ols-limestone)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-10">
          {/* Sidebar */}
          <aside className="space-y-6">
            <div className="bg-white p-6 rounded border" style={{ borderColor: "var(--ols-hairline)" }}>
              <p className="ols-eyebrow mb-3">Direct Contact</p>
              <p className="mb-4">
                <a href="tel:+18175775024" data-testid="contact-office-phone" className="flex items-center gap-3 text-[15px] font-semibold" style={{ color: "var(--ols-navy)" }}>
                  <Phone className="h-4 w-4" style={{ color: "var(--ols-copper)" }} />
                  (817) 577-5024
                </a>
              </p>
              <p>
                <a href="mailto:info@onwardland.com" data-testid="contact-office-email" className="flex items-center gap-3 text-[14px] break-all" style={{ color: "var(--ols-navy)" }}>
                  <Mail className="h-4 w-4" style={{ color: "var(--ols-copper)" }} />
                  info@onwardland.com
                </a>
              </p>
              <p className="text-[13px] mt-4" style={{ color: "var(--ols-body-muted)" }}>Fort Worth, Texas</p>
            </div>

            <div className="bg-white p-6 rounded border" style={{ borderColor: "var(--ols-hairline)" }}>
              <p className="ols-eyebrow mb-3">What Happens Next</p>
              <ol className="space-y-3 text-[13px]" style={{ color: "var(--ols-body)" }} data-testid="contact-next-steps">
                <li className="flex gap-3"><span className="font-bold" style={{ color: "var(--ols-copper)" }}>1.</span> Follow-up target: one business day.</li>
                <li className="flex gap-3"><span className="font-bold" style={{ color: "var(--ols-copper)" }}>2.</span> Confirm scope, sources and confidentiality.</li>
                <li className="flex gap-3"><span className="font-bold" style={{ color: "var(--ols-copper)" }}>3.</span> Arrange file exchange and next steps.</li>
              </ol>
            </div>

            <div className="bg-white p-6 rounded border" style={{ borderColor: "var(--ols-hairline)" }} data-testid="contact-talent-pool-card">
              <p className="ols-eyebrow mb-2" style={{ color: "#C8102E" }}>Land Professionals</p>
              <p className="text-[14px] mb-4" style={{ color: "var(--ols-body)" }}>
                Right-of-way agent, title, GIS, or field access professional? Join the Onward Talent Pool.
              </p>
              <Link to="/talent-network?view=professional&source=contact_sidebar#professional-application" className="ols-btn-talent w-full" style={{ display: "inline-flex" }} data-testid="contact-talent-cta">
                Join the Talent Pool
              </Link>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-2 bg-white p-5 sm:p-8 rounded border min-w-0" style={{ borderColor: "var(--ols-hairline)" }}>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <p className="text-[13px]" style={{ color: "var(--ols-body-muted)" }}>
                Name, work email and brief description are required. Other fields are optional.
              </p>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field id="name" label="Name *" required value={formData.name} onChange={handleChange} placeholder="Your name" />
                <Field id="company" label="Company" value={formData.company} onChange={handleChange} placeholder="Company name" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field id="email" label="Work Email *" type="email" required value={formData.email} onChange={handleChange} placeholder="you@company.com" />
                <Field id="phone" label="Phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="(555) 000-0000" />
              </div>

              {isCorridor ? <p className="text-sm font-semibold" data-testid="first-look-interest">Interest: Complimentary PathScore First Look</p> : <div className="grid sm:grid-cols-2 gap-5">
                <Select id="project_type" label="Project Type" value={formData.project_type} onChange={handleChange} options={PROJECT_TYPE_OPTIONS} placeholder="Select project type" testId="form-project-type" />
                <Select id="service_needed" label="Service Needed" value={formData.service_needed} onChange={handleChange} options={SERVICE_OPTIONS} placeholder="Select service" testId="form-service-needed" />
              </div>}

              {isCorridor && <Select id="route_available" label="Route Availability (optional)" value={formData.route_available} onChange={handleChange} options={["Route or KMZ available", "Endpoints only", "Not yet determined"]} placeholder="Select if known" testId="form-route-availability" />}

              <div className="grid sm:grid-cols-2 gap-5">
                <Field id="location" label="Project Location" value={formData.location} onChange={handleChange} placeholder="County, state, or region" />
                <Field id="acreage" label={isCorridor ? "Approximate Length (optional)" : "Project Size"} value={formData.acreage} onChange={handleChange} placeholder={isCorridor ? "Approximate miles, if known" : "Acres, miles, or MW"} />
              </div>

              {!isCorridor && <>
              <div className="grid sm:grid-cols-2 gap-5">
                <Select id="project_stage" label="Current Project Stage" value={formData.project_stage} onChange={handleChange} options={PROJECT_STAGE_OPTIONS} placeholder="Select current stage" testId="form-project-stage" />
                <div>
                  <label className="block text-[13px] font-semibold mb-2" style={{ color: "var(--ols-navy)" }}>Route / Site Alternatives Being Considered</label>
                  <div className="flex gap-3">
                    {["Yes", "No", "Not sure"].map((opt) => (
                      <label key={opt} className={`flex-1 border rounded px-3 py-2.5 text-[14px] cursor-pointer text-center ${formData.alternatives === opt ? "bg-[var(--ols-navy)] text-white" : "bg-white"}`} style={{ borderColor: formData.alternatives === opt ? "var(--ols-navy)" : "var(--ols-hairline)", color: formData.alternatives === opt ? "#fff" : "var(--ols-navy)" }}>
                        <input type="radio" name="alternatives" value={opt} checked={formData.alternatives === opt} onChange={handleChange} className="sr-only" data-testid={`form-alternatives-${opt.toLowerCase().replace(/\s+/g, "-")}`} />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="timeline" className="block text-[13px] font-semibold mb-2" style={{ color: "var(--ols-navy)" }}>Schedule / Timeline</label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border rounded text-[15px]"
                    style={{ borderColor: "var(--ols-hairline)", color: "var(--ols-navy)", background: "#fff" }}
                    data-testid="form-timeline"
                  >
                    <option value="">Select timeline</option>
                    <option>ASAP</option>
                    <option>1-3 Months</option>
                    <option>3-6 Months</option>
                    <option>6+ Months</option>
                    <option>Still in planning phase</option>
                  </select>
                </div>
                <Select id="preferred_contact" label="Preferred Contact Method" value={formData.preferred_contact} onChange={handleChange} options={PREFERRED_CONTACT_OPTIONS} placeholder="Select preferred contact" testId="form-preferred-contact" />
              </div>
              </>}

              <label className="flex items-start gap-3 text-sm" style={{ color: "var(--ols-body)" }}>
                <input type="checkbox" name="nda_requested" checked={formData.nda_requested} onChange={handleChange} className="mt-1" style={{ accentColor: "#C8102E" }} data-testid="form-nda-request" />
                I would like to arrange an NDA before sharing sensitive project materials. (Optional)
              </label>

              <div>
                <label htmlFor="message" className="block text-[13px] font-semibold mb-2" style={{ color: "var(--ols-navy)" }}>Brief Project Description *</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={isCorridor
                    ? "General location and the corridor decision you need to make. No confidential title data, please."
                    : "Describe your project: scope, key challenges, and any specific requirements."}
                  className="w-full px-4 py-3 border rounded text-[15px]"
                  style={{ borderColor: "var(--ols-hairline)", color: "var(--ols-navy)", background: "#fff" }}
                  data-testid="form-message"
                />
              </div>

              {error && <p role="alert" className="text-red-600 text-[14px]" data-testid="form-error">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="ols-btn-primary w-full justify-center"
                data-testid="form-submit"
              >
                {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</>) : (<><span className="min-w-0 whitespace-normal">{isCorridor ? "Get a Complimentary PathScore First Look" : "Talk to Onward"}</span><ArrowRight className="h-4 w-4 shrink-0" /></>)}
              </button>
              {isCorridor && <p className="text-xs text-center" style={{ color: "var(--ols-body)" }} data-testid="first-look-submit-scope">Complimentary preliminary screening. Full PathScore is separately scoped paid work.</p>}

              <p className="text-[12px] text-center" style={{ color: "var(--ols-body-muted)" }} data-testid="contact-privacy-note">
                Please do not send confidential project materials through this form. For questions about handling your inquiry, contact <a href="mailto:info@onwardland.com" className="underline" data-testid="contact-privacy-email">info@onwardland.com</a>. An NDA can be arranged before sensitive files are shared.
              </p>
            </form>
          </div>
        </div>
      </section>
    </>
  );
};

const Field = ({ id, label, required, value, onChange, type = "text", placeholder }) => (
  <div>
    <label htmlFor={id} className="block text-[13px] font-semibold mb-2" style={{ color: "var(--ols-navy)" }}>{label}</label>
    <input
      id={id}
      name={id}
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={`form-${id}`}
      className="w-full px-4 py-3 border rounded text-[15px]"
      style={{ borderColor: "var(--ols-hairline)", color: "var(--ols-navy)", background: "#fff" }}
    />
  </div>
);

const Select = ({ id, label, value, onChange, options, placeholder, testId }) => (
  <div>
    <label htmlFor={id} className="block text-[13px] font-semibold mb-2" style={{ color: "var(--ols-navy)" }}>{label}</label>
    <select
      id={id}
      name={id}
      value={value}
      onChange={onChange}
      data-testid={testId || `form-${id}`}
      className="w-full px-4 py-3 border rounded text-[15px]"
      style={{ borderColor: "var(--ols-hairline)", color: "var(--ols-navy)", background: "#fff" }}
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  </div>
);

export default Contact;
